import { PropertyType } from '~/client/graph/types.generated'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'
import CompaniesStore from '~/client/src/shared/stores/domain/Companies.store'
import ProjectMembersStore from '~/client/src/shared/stores/domain/ProjectMembers.store'
import TagsStore from '~/client/src/shared/stores/domain/Tags.store'
import ProjectDateStore from '~/client/src/shared/stores/ui/ProjectDate.store'
import { parseDateValue } from '~/client/src/shared/utils/dateValueParser'
import {
  getHierarchyAccessors,
  IHierarchyAccessors,
} from '~/client/src/shared/utils/hierarchyAccessors'
import {
  buildOptionsByValue,
  HierarchyTreeOption,
} from '~/client/src/shared/utils/hierarchyTree'

import {
  getResourceFieldDefinition,
  IResourceRuntimeContext,
} from '../definitions'
import { getResourceLinkTarget, IResourceLinkConfig } from '../resourceLink'
import {
  formatResourceField,
  RESOURCE_EMPTY_VALUE,
} from './formatResourceField'

interface IResourceDisplayProperty {
  name: string
  propertyType: PropertyType | string
  configuration?: {
    options?: HierarchyTreeOption[]
    unit?: string
    resourceLink?: IResourceLinkConfig | null
  }
}

interface ICreateResourceDisplayValueOptions {
  properties: IResourceDisplayProperty[]
  resourceTypeId?: string
  runtimeContext: IResourceRuntimeContext
  projectDateStore?: ProjectDateStore
  projectMembersStore?: ProjectMembersStore
  companiesStore?: CompaniesStore
  tagsStore?: TagsStore
}

const toFallbackDisplayValue = (
  property: IResourceDisplayProperty | undefined,
  value: any,
  projectDateStore?: ProjectDateStore,
  projectMembersStore?: ProjectMembersStore,
  companiesStore?: CompaniesStore,
  tagsStore?: TagsStore,
  hierarchyAccessors?: IHierarchyAccessors,
  selectOptionsByValue?: Map<string, HierarchyTreeOption>,
): string => {
  if (value === null || value === undefined || value === '') {
    return RESOURCE_EMPTY_VALUE
  }

  if (property?.propertyType === PropertyType.User) {
    const member = projectMembersStore?.getById(String(value))
    if (member) {
      return User.getFullName(member)
    }
    return Localization.translator.unknownUser
  }

  if (property?.propertyType === PropertyType.Company) {
    return companiesStore?.getCompanyNameById(String(value)) || String(value)
  }

  if (property?.propertyType === PropertyType.ResourceLink) {
    const target = getResourceLinkTarget(property.configuration)
    if (target) return target.formatValue(String(value), { tagsStore })
  }

  if (
    (property?.propertyType === PropertyType.Select ||
      property?.propertyType === PropertyType.Hierarchy) &&
    property.configuration?.options
  ) {
    const getOptionLabel = (optionValue: string): string => {
      if (property.propertyType === PropertyType.Hierarchy) {
        return hierarchyAccessors?.getPathLabel(optionValue) ?? optionValue
      }
      return selectOptionsByValue?.get(optionValue)?.label || optionValue
    }

    if (Array.isArray(value)) {
      return value.map(item => getOptionLabel(String(item))).join(', ')
    }

    return getOptionLabel(String(value))
  }

  if (property?.propertyType === PropertyType.Checkbox) {
    const boolValue = value === true || value === 'true'
    return boolValue ? Localization.translator.yes : Localization.translator.no
  }

  if (property?.propertyType === PropertyType.Datetime) {
    if (!value && value !== false) {
      return RESOURCE_EMPTY_VALUE
    }
    if (projectDateStore) {
      const date = parseDateValue(value, projectDateStore)
      if (date) {
        return projectDateStore.getWeekdayMonthDayAndYearToDisplay(date)
      }
    }
    return RESOURCE_EMPTY_VALUE
  }

  if (property?.propertyType === PropertyType.File) {
    if (!Array.isArray(value) || value.length === 0) {
      return RESOURCE_EMPTY_VALUE
    }
    return value.length === 1
      ? value[0]?.fileName || '1 file'
      : `${value.length} files`
  }

  if (property?.configuration?.unit) {
    return `${value} ${property.configuration.unit}`
  }

  return String(value)
}

export const createResourceDisplayValue = ({
  properties,
  resourceTypeId,
  runtimeContext,
  projectDateStore,
  projectMembersStore,
  companiesStore,
  tagsStore,
}: ICreateResourceDisplayValueOptions) => {
  const propertiesByName = new Map(
    properties.map(property => [property.name, property]),
  )

  const hierarchyByName = new Map<string, IHierarchyAccessors>()
  properties.forEach(property => {
    if (
      property.propertyType === PropertyType.Hierarchy &&
      property.configuration?.options
    ) {
      hierarchyByName.set(
        property.name,
        getHierarchyAccessors(property.configuration.options),
      )
    }
  })

  const selectOptionsByName = new Map<
    string,
    Map<string, HierarchyTreeOption>
  >()
  properties.forEach(property => {
    if (
      property.propertyType !== PropertyType.Hierarchy &&
      property.configuration?.options
    ) {
      selectOptionsByName.set(
        property.name,
        buildOptionsByValue(property.configuration.options),
      )
    }
  })

  return (propertyName: string, value: any): string => {
    const property = propertiesByName.get(propertyName)
    const fallbackDisplayValue = toFallbackDisplayValue(
      property,
      value,
      projectDateStore,
      projectMembersStore,
      companiesStore,
      tagsStore,
      hierarchyByName.get(propertyName),
      selectOptionsByName.get(propertyName),
    )

    const fieldDefinition = getResourceFieldDefinition(
      resourceTypeId,
      propertyName,
    )

    return formatResourceField({
      fieldDefinition,
      value,
      fallbackDisplayValue,
      runtimeContext,
    })
  }
}
